import { useCallback, useRef } from 'react';
import type { FocusEvent, PointerEvent } from 'react';

const SLOT_PLACEHOLDERS = 'DMYHhms';

const wireDigitEditing = (input: HTMLInputElement) => {
  const isSlot = (placeholder: string, i: number) => SLOT_PLACEHOLDERS.includes(placeholder[i] ?? '');
  const hasCleared = (value: string, placeholder: string) =>
    [...value].some((ch, i) => isSlot(placeholder, i) && ch === placeholder[i]);

  let lastGood = hasCleared(input.value, input.placeholder) ? '' : input.value;

  const step = (placeholder: string, from: number, dir: 1 | -1) => {
    for (let i = from; i >= 0 && i < placeholder.length; i += dir) {
      if (isSlot(placeholder, i)) {
        return i;
      }
    }
    return -1;
  };

  input.addEventListener('keydown', (event) => {
    const { key } = event;
    const isDigit = /^[0-9]$/.test(key);
    const isClear = key === 'Backspace' || key === 'Delete';
    if ((!isDigit && !isClear) || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const { value, placeholder } = input;
    if (!placeholder || placeholder.length !== value.length) {
      return;
    }
    const cleared = hasCleared(value, placeholder);
    if (!cleared) {
      lastGood = value;
    }

    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? start;
    // Whole-value selection (first keystroke after Tab) stays with the mask's own replace/clear.
    if (end - start > 1) {
      return;
    }
    // Typing over a fully valid value stays a native 1-for-1 overwrite handled by the mask.
    if (isDigit && !cleared) {
      return;
    }

    let pos: number;
    if (end > start) {
      pos = start;
      if (!isDigit && key === 'Backspace' && value[pos] === placeholder[pos]) {
        pos = step(placeholder, pos - 1, -1);
      }
    } else if (key === 'Backspace') {
      pos = step(placeholder, start - 1, -1);
    } else {
      pos = step(placeholder, start, 1);
    }

    event.preventDefault();
    if (pos < 0) {
      return;
    }

    const replacement = isDigit ? key : placeholder[pos];
    const next = value.slice(0, pos) + replacement + value.slice(pos + 1);

    if (!hasCleared(next, placeholder)) {
      // Fully filled again: let the mask parse/validate it and emit the new value.
      input.value = next;
      input.setSelectionRange(pos + 1, pos + 1);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      lastGood = input.value;
      return;
    }

    input.value = next;
    let nextPos = -1;
    if (isDigit) {
      nextPos = step(placeholder, pos + 1, 1);
    }
    const caret = nextPos >= 0 ? nextPos : pos;
    input.setSelectionRange(caret, caret + 1);
  });

  // An unfinished edit must not outlive the field: fall back to the last complete value.
  input.addEventListener('blur', () => {
    if (hasCleared(input.value, input.placeholder)) {
      input.value = lastGood;
    }
  });
};

/**
 * Edit behaviour for masked Zafire `ZfInput` fields (type `date`/`time`):
 * - Tab focus selects the whole value, so the first keystroke replaces it.
 * - Click/tap, and every keystroke after, keeps exactly ONE character selected. The mask (imask)
 *   doesn't overwrite, and any edit that shrinks the string mid-value makes it misparse the
 *   following blocks, so each edit must be a same-length, 1-for-1 overwrite.
 * - Backspace/Delete can't go through the mask: it can't hold a half-cleared block (`29.0M.2026`
 *   re-parses as `29.02.026Y`). `wireDigitEditing` swaps only the targeted character for its
 *   placeholder and gives the value back to the mask once no placeholders remain. An unfinished
 *   edit is reverted on blur, as the component does for an incomplete mask.
 *
 * @example
 * const dateFieldCaretReset = useCaretResetOnFocus();
 * <ZfInput type="date" {...dateFieldCaretReset} ... />
 */
export const useCaretResetOnFocus = () => {
  const isPointerFocus = useRef(false);
  const wiredInputs = useRef(new WeakSet<HTMLInputElement>());

  const selectSingleCharAt = (input: HTMLInputElement, caret: number) => {
    const value = input.value;
    if (caret < value.length) {
      input.setSelectionRange(caret, caret + 1);
    } else if (caret > 0) {
      input.setSelectionRange(caret - 1, caret);
    }
  };

  const onPointerDown = useCallback((_event: PointerEvent<HTMLElement>) => {
    isPointerFocus.current = true;
  }, []);

  const onFocus = useCallback((event: FocusEvent<HTMLElement>) => {
    const hostElement = event.currentTarget;
    const viaPointer = isPointerFocus.current;
    isPointerFocus.current = false;

    // Deferred so it runs after the component's own focus handler re-initializes the mask.
    setTimeout(() => {
      const input = hostElement.shadowRoot?.querySelector('input');
      if (!input) {
        return;
      }

      // Wired once per underlying <input> (tracked in a WeakSet since this hook's handlers are
      // often shared across more than one field, e.g. startDate/endDate) and left attached for
      // the element's lifetime, since it persists across the mask's own focus/blur reinit cycles.
      if (!wiredInputs.current.has(input)) {
        wiredInputs.current.add(input);
        wireDigitEditing(input);
        input.addEventListener('input', () => {
          selectSingleCharAt(input, input.selectionStart ?? 0);
        });
      }

      if (viaPointer) {
        return;
      }
      input.setSelectionRange(0, input.value.length);
    }, 0);
  }, []);

  // Fires after the browser has resolved the click's caret position and after any focus-triggered
  // mask reinit has already run synchronously, so the current selection reflects the actual click.
  const onPointerUp = useCallback((event: PointerEvent<HTMLElement>) => {
    const input = event.currentTarget.shadowRoot?.querySelector('input');
    if (!input) {
      return;
    }
    selectSingleCharAt(input, input.selectionStart ?? 0);
  }, []);

  return { onFocus, onPointerDown, onPointerUp };
};

export default useCaretResetOnFocus;
